import type {
  PaginatedProducts,
  Product,
  ProductInput,
  ProductPriceInput,
} from '@shop-monorepo/types';
import { useMutation, useQuery } from '@tanstack/react-query';
import { message } from 'antd';

import { apiClient } from '../services/api';

const PRODUCT_QUERY_KEY = 'products'; // Use a single key for all products queries

interface ProductsQueryParams {
  page?: number;
  limit?: number;
  filters?: { brand?: string; category?: string; isActive?: boolean; isNew?: boolean; q?: string };
  sorter?: { field?: string; direction?: 'asc' | 'desc' };
}

export const useProducts = (params: ProductsQueryParams) => {
  return useQuery<PaginatedProducts, Error>({
    queryKey: [PRODUCT_QUERY_KEY, params], // Include params in query key for pagination/filtering
    queryFn: async () => {
      const { page = 1, limit = 10, filters, sorter } = params;
      const data = await apiClient.products.all(page, limit, filters, sorter);
      return data;
    },
    // Keep previous data when page/filters change, to avoid flickering
    // This is optional, but improves UX for pagination/filtering
    placeholderData: (previousData) => previousData,
  });
};

export const useCheckProductNameDuplicates = (name: string, excludeProductId?: number) => {
  const trimmed = name.trim();
  return useQuery<PaginatedProducts, Error, Product[]>({
    queryKey: [PRODUCT_QUERY_KEY, 'duplicate-check', trimmed],
    queryFn: () => apiClient.products.all(1, 5, { q: trimmed }),
    enabled: trimmed.length >= 3,
    select: (data) => data.hits.filter((p) => p.id !== excludeProductId),
  });
};

export const useProduct = (slug: string) => {
  return useQuery<Product, Error>({
    queryKey: [PRODUCT_QUERY_KEY, slug],
    queryFn: () => apiClient.products.getBySlug(slug),
    enabled: !!slug,
  });
};

export const useCreateProduct = () => {
  return useMutation<Product, Error, ProductInput>({
    mutationFn: (newProduct) => apiClient.products.create(newProduct),
    meta: { invalidates: [[PRODUCT_QUERY_KEY]] },
    onSuccess: () => {
      message.success('Продукт успішно додано!');
    },
    onError: (error) => {
      message.error(`Не вдалося додати продукт: ${error.message}`);
    },
  });
};

export const useUpdateProduct = () => {
  return useMutation<Product, Error, { id: number; product: ProductInput }>({
    mutationFn: ({ id, product }) => apiClient.products.update(id, product),
    meta: { invalidates: [[PRODUCT_QUERY_KEY]] },
    onSuccess: () => {
      message.success('Продукт успішно оновлено!');
    },
    onError: (error) => {
      message.error(`Не вдалося оновити продукт: ${error.message}`);
    },
  });
};

export const useUpdateProductPrice = () => {
  return useMutation<Product, Error, { id: number; price: ProductPriceInput }>({
    mutationFn: ({ id, price }) => apiClient.products.updatePrice(id, price),
    meta: { invalidates: [[PRODUCT_QUERY_KEY]] },
    onSuccess: () => {
      message.success('Ціну оновлено');
    },
    onError: (error) => {
      message.error(`Не вдалося оновити ціну: ${error.message}`);
    },
  });
};

export const useUpdateProductActive = () => {
  return useMutation<Product, Error, { id: number; isActive: boolean }>({
    mutationFn: ({ id, isActive }) => apiClient.products.updateActive(id, isActive),
    meta: { invalidates: [[PRODUCT_QUERY_KEY]] },
    onSuccess: (product) => {
      message.success(product.isActive ? 'Продукт активовано' : 'Продукт деактивовано');
    },
    onError: (error) => {
      message.error(`Не вдалося змінити активність: ${error.message}`);
    },
  });
};

export const useGenerateProductDescription = () => {
  return useMutation<{ description: string }, Error, string>({
    mutationFn: (name) => apiClient.products.generateDescription(name),
    onError: (error) => {
      message.error(`Не вдалося згенерувати опис: ${error.message}`);
    },
  });
};

export const useDeleteProduct = () => {
  return useMutation<
    { message: string },
    Error,
    number // The id of the product to delete
  >({
    mutationFn: (id) => apiClient.products.delete(id),
    meta: { invalidates: [[PRODUCT_QUERY_KEY]] },
    onSuccess: () => {
      message.success('Продукт успішно видалено!');
    },
    onError: (error) => {
      message.error(`Не вдалося видалити продукт: ${error.message}`);
    },
  });
};
